'use client';

/**
 * ToolTimeoutChip — the collapsed state of the tool-timeout recovery flow
 * (lib/tool-timeout-view.ts's ToolTimeoutView.COLLAPSED), shown when the
 * card was ignored and the user kept talking. Deliberately NOT a
 * StandardCard: a quiet, low-profile inline chip that never competes with
 * or blocks the conversation, one tap ("Try again") away from re-entering
 * the flow (lib/tool-timeout-view.ts's RESUME action jumps straight back
 * into RETRYING — there's nothing else on this chip to re-expand into).
 *
 * The whole chip is a single button (not just the "Try again" text) so it's
 * one focusable control with one accessible name, rather than a decorative
 * label sitting next to a separately-focusable link.
 *
 * Copy review: used to take a `toolLabel` prop and render
 * "{Capitalized tool label} unavailable" (e.g. "Course search
 * unavailable") — exposing an internal service name as something that can
 * be "unavailable" to the user. Dropped `toolLabel` entirely in favor of
 * a generic label (see tool-timeout-offer.tsx's own copy-review note for
 * the full `tool_label` decision).
 */
import { WifiOff } from 'lucide-react';
import { V3_COLORS } from '@/lib/design-tokens';

interface ToolTimeoutChipProps {
  onRetry: () => void;
}

const LABEL = "Couldn't look that up";

export function ToolTimeoutChip({ onRetry }: ToolTimeoutChipProps) {
  return (
    <div className="mx-auto flex w-full max-w-[85%] justify-start sm:max-w-[70%]">
      <button
        type="button"
        onClick={onRetry}
        aria-label={`${LABEL}. Try again.`}
        // 44px, not 36px — every other tap target in this family respects
        // the 44px minimum (see tool-timeout-choose.tsx's OptionTile / Back
        // link); this one was the odd one out (FINDING C).
        className="inline-flex min-h-[44px] items-center gap-[8px] rounded-[20px] border-[1px] border-[#eadfce] bg-white px-[12px] py-[7px] text-[12.5px] font-semibold"
      >
        <span
          aria-hidden="true"
          className="flex size-[18px] shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: V3_COLORS.standardCardNeutralBg }}
        >
          <WifiOff
            aria-hidden="true"
            className="size-[10px]"
            style={{ color: V3_COLORS.standardCardNeutralIcon }}
          />
        </span>
        <span aria-hidden="true" style={{ color: V3_COLORS.secondary }}>
          {LABEL}
        </span>
        <span aria-hidden="true" style={{ color: V3_COLORS.orange }}>
          Try again
        </span>
      </button>
    </div>
  );
}
